import { useEffect, useState} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Container, 
  Title,
  Stack,
  Text,
  Collapse,
  Table,
  Group,
  Anchor,
  Grid,
  Button,
  Box,
} from '@mantine/core';
import CardButton from '../components/CardButton';
import useActiveProcess from '../hooks/useActiveProcess';
import { IconSwitchHorizontal, IconCalendar } from '@tabler/icons-react';
import useFeatureAccess from '../hooks/useFeatureAccess';
import { formatDate } from '../utils/dateUtils';

const BienvenidoReportes = () => {
  const navigate = useNavigate();
  const {user, permissions} = useSelector((state) => state.auth);

  const activeProcess = useActiveProcess();
  const evd = useFeatureAccess('evidences');   // { canEnter, canEdit, readOnly, reason }
  const eva = useFeatureAccess('evaluacion');

  const [datesTableVisible, setDatesTableVisible] = useState(false);

  const getStageEndDate = (orden) => {
    if (!activeProcess?.etapas || !Array.isArray(activeProcess.etapas)) return null;
    const orderedStages = [...activeProcess.etapas].sort((a, b) => a.orden - b.orden);
    const stage = orderedStages.find(e => e.orden === orden);
    return stage?.fechaCierre || null;
  };

  const etapa1EndDate = getStageEndDate(1);
  const etapa2EndDate = getStageEndDate(2); 

  const cardInfo = [
    {
      title:'Etapa 1: Gestión de evidencias', 
      content:(<Stack>
        <Text>Analice cada KPI y elabore evidencias.</Text>
        <Text> 
          Toda evidencia constituye un <b>tangible</b> que porta información o pruebas referidas al nivel de cumplimiento de cada indicador que se está evaluando.
        </Text>
        <Text>
          Cuando la <b>información</b> recabada, ya sea cualitativa o cuantitativa es <b>analizada</b>, se convierte en <b>evidencia</b>. La información puede provenir de <b>diferentes fuentes</b> que deben ser <b>accesibles</b> para quien desee consultarlas y así dar transparencia al indicador. 
        </Text>
        <Text>
          Priorice aquellas evidencias que demuestren un impacto directo y significativo sobre su unidad académica. 
        </Text>
      </Stack>),
      disabled: !evd.canEnter,
      onClick: () => evd.canEnter && navigate('/backoffice/evidences'),
    },
    {
      title:'Etapa 2: Proceso de evaluación',
      content:(
        <Stack>
          <Text>
            Identifique fortalezas y debilidades para cada criterio de calidad, teniendo en cuenta las evidencias cuantitativas y cualitativas aportadas en el proceso de “Gestión de Evidencias” para los KPI vinculados a cada criterio. 
          </Text>
          <Text>  
            A partir de la evaluación efectuada diseñe propuestas de mejora continua para cada criterio de calidad que incluyan objetivos, estrategias, metas calendarizadas, recursos requeridos y responsables a cargo, priorizando el abordaje de las debilidades detectadas. 
          </Text>
        </Stack>
      ),
      disabled: !eva.canEnter,
      onClick: () => eva.canEnter && navigate('/backoffice/evaluation'),
    }
  ]
  
  return (
    <div>
      <section className='bienvenida'>
        <div className='bienvenida-header'>
          <Group justify={'space-between'}>
            <Stack gap={5}>
              <Title fw={'400'}>¡Bienvenido/a, <Text span inherit c='#e72166'>{user.username}</Text>!</Title>
              <Text>Se encuentra en: <Text span fw={600}>{activeProcess.nombre}</Text></Text>
            </Stack>
            <Button variant='default' leftSection={<IconSwitchHorizontal size={18}/>} onClick={() => navigate('/procesos-evaluacion')}>
              Cambiar de Proceso
            </Button>
          </Group>
        </div>
        <div className='bienvenida-content'>
          <Stack gap="xs">
            <Text>
              Esta plataforma fue diseñada para el monitoreo de los objetivos estratégicos a cargo de las autoridades de cada Facultad, basada en una matriz de <span id='highlight'>indicadores clave de rendimiento (Key Performance Indicators o KPI)</span>.
            </Text>
            <Text>
              Los KPI se correlacionan con los criterios de calidad previstos en el Plan Estratégico Institucional, constituyendo de este modo un componente crítico que brinda una visión cuantitativa y cualitativa del rendimiento y la eficacia de las cuatro funciones sustantivas: gobernanza, docencia, investigación y extensión.
            </Text>
            <Text>
              
            </Text>
            {/*<Group>
              <Text>
                Seleccione la Etapa 1 para comenzar, para {datesTableVisible ? 'ocultar' : 'mostrar'} informacion sobre fechas y cargos sobre las etapas, presione <Anchor c={'#e72166'} onClick={() => setDatesTableVisible(!datesTableVisible)}>aqui <IconCalendarStats size={16}/></Anchor>
              </Text>
            </Group>*/}
            
          </Stack>
        </div>
      </section>
      <Group>
        <Collapse in={datesTableVisible} transitionDuration={300} transitionTimingFunction="linear">
          <div className='card-base' style={{marginBottom: '2rem'}}>
            <Table  withColumnBorders >
              <Table.Thead>
                <Table.Tr>
                  <Table.Th rowSpan={1} bg={'#f8f8f8'}>Etapas</Table.Th>
                  <Table.Th colSpan={3} ta={'center'} bg={'#e72166'} c={'#ffffff'}>Gestión de Evidencias</Table.Th>
                  <Table.Th colSpan={3} ta={'center'} bg={'#e72166'} c={'#ffffff'}>Proceso de Evaluación</Table.Th>
                </Table.Tr>
                <Table.Tr>
                  <Table.Th bg={'#f8f8f8'}></Table.Th>
                  <Table.Th>Carga de evidencias por cada KPI</Table.Th>
                  <Table.Th>Monitoreo de las evidencias cargadas</Table.Th>
                  <Table.Th>Selección de evidencias por KPI</Table.Th>
                  <Table.Th>Elaboración de informes con debilidades y fortalezas y planes de mejora de cada carrera por cada criterio de calidad</Table.Th>
                  <Table.Th>Evaluación integral de la Facultad con debilidades y fortalezas y planes de mejora por cada criterio de calidad</Table.Th>
                  <Table.Th>Entrevistas rendición de cuentas</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                <Table.Tr>
                  <Table.Th bg={'#f8f8f8'}>Directores</Table.Th>
                  <Table.Td>Desde el 4 de febrero</Table.Td>
                  <Table.Td></Table.Td>
                  <Table.Td></Table.Td>
                  <Table.Td>Desde el 17 de Marzo</Table.Td>
                  <Table.Td>9 de Mayo cierre de cada proceso</Table.Td>
                  <Table.Td>Junio (días a definir) - Muestra probabilística</Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Th bg={'#f8f8f8'}>Decanos</Table.Th>
                  <Table.Td></Table.Td>
                  <Table.Td>Desde el 17 de febrero</Table.Td>
                  <Table.Td>Desde el 17 de marzo</Table.Td>
                  <Table.Td></Table.Td>
                  <Table.Td>9 de Mayo cierre Evaluación Integral de la facultad</Table.Td>
                  <Table.Td>Del 4 al 6 de junio</Table.Td>
                </Table.Tr>
              </Table.Tbody>
            </Table>
          </div>
        </Collapse>
      </Group>
      <Grid>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <div className='card-button-container' >
            <CardButton
              title={cardInfo[0].title}
              content={cardInfo[0].content}
              action={cardInfo[0].disabled ? null : cardInfo[0].onClick}
              endDate={etapa1EndDate}
            />
          </div>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <div className='card-button-container'>
            <CardButton
              title={cardInfo[1].title}
              content={cardInfo[1].content}
              action={cardInfo[1].disabled ? null : cardInfo[1].onClick}
              endDate={etapa2EndDate}
            />
          </div>
        </Grid.Col>
      </Grid>
    </div>
  );
};

export default BienvenidoReportes;